import React from 'react';
import { motion } from 'framer-motion';
import { X } from 'lucide-react';
import { conflictingSources, needsIntro, needsList, painPoints, searchQuestions, symptoms, systemShows } from '../data/painPoints';
const reveal = {
  initial: {
    opacity: 0,
    y: 16
  },
  whileInView: {
    opacity: 1,
    y: 0
  },
  viewport: {
    once: true
  },
  transition: {
    duration: 0.3,
    ease: [0.23, 1, 0.32, 1]
  }
} as const;
export function PainPoints() {
  return <section className="py-24 px-4 bg-[#F3F5F7]" data-id="element-183" data-mp="1xwgc6udciflt">
      <div className="max-w-4xl mx-auto" data-id="element-184" data-mp="21zlaslub1x56">
        <motion.div {...reveal} className="text-center mb-12" data-id="element-185" data-mp="220o22uvji1lm">
          <h2 className="text-3xl md:text-4xl font-bold tracking-tight mb-6 text-[#16191E] uppercase" data-id="element-186" data-mp="1o0d4za3mzqs0">
            Still Spending Hours Preparing{' '}
            <span className="text-[#E8590C]" data-id="element-187" data-mp="1t0hq5dfhp9ok">Your Welding Classes</span>?
          </h2>
          <div className="text-lg text-slate-700 leading-relaxed bg-white p-6 md:p-8 rounded-2xl border border-[#D5DAE0] text-left space-y-4" data-id="element-188" data-mp="3ibi7uc6wfz6a">
            <p data-id="element-189" data-mp="1z05770l8kmxi">
              {conflictingSources.map(line => <React.Fragment key={line} data-id="element-190" data-mp="1yyc2hqu6ocs5">
                  {line}
                  <br data-id="element-191" data-mp="1u12coa2vtz3s" />
                </React.Fragment>)}
            </p>
            <p className="text-[#16191E] font-semibold" data-id="element-192" data-mp="27rp37eyhoios">
              {symptoms.map((line, index) => <React.Fragment key={line} data-id="element-193" data-mp="23rx2tpj91iui" data-mpi={index}>
                  {line}
                  {index < symptoms.length - 1 && <br data-id="element-194" data-mp="1g2cfyvjz61fy" />}
                </React.Fragment>)}
            </p>
            <p className="text-[#16191E] font-semibold" data-id="element-195" data-mp="1b416ilxldsy9">{needsIntro}</p>
            <p data-id="element-196" data-mp="1p30m7434mxia">
              {needsList.map((line, index) => <React.Fragment key={line} data-id="element-197" data-mp="1p04nvenda6tb" data-mpi={index}>
                  {line}
                  {index < needsList.length - 1 && <br data-id="element-198" data-mp="1k5qv7k03adsc" />}
                </React.Fragment>)}
            </p>
            <p data-id="element-199" data-mp="30jqssobgprcf">Suddenly, you&apos;re facing the same questions again:</p>
            <ul className="space-y-2 pl-1" data-id="element-200" data-mp="2vi1hr8o8pdoc">
              {searchQuestions.map(question => <li key={question} className="flex items-start gap-3 text-[#16191E] font-semibold" data-id="element-201" data-mp="1t4n6aos0zddh">
                  <span className="w-1.5 h-1.5 rounded-full bg-[#F28A2E] shrink-0 mt-3" aria-hidden="true" data-id="element-202" data-mp="1y2on131zb0ls" />
                  <span data-id="element-203" data-mp="226mmxfz4offw">{question}</span>
                </li>)}
            </ul>
            <p className="text-[#B8480A] font-semibold" data-id="element-204" data-mp="274egsfjc2bmq">
              You already know how important quality welding education is.
            </p>
            <p data-id="element-205" data-mp="1efzbwltx19p4">But building every class from scratch can become a second job.</p>
          </div>
        </motion.div>

        <motion.div {...reveal} data-id="element-206" data-mp="1jf3kpbcxh5ex">
          <h3 className="text-xl font-bold text-[#16191E] mb-8 text-center uppercase tracking-wide" data-id="element-207" data-mp="1jdsg59bnrtmr">
            The Real Cost of Teaching Without a Complete System
          </h3>
          <div className="overflow-hidden rounded-xl border border-[#D5DAE0] bg-white shadow-sm shadow-[#16191E]/5" data-id="element-208" data-mp="qpwowk4uytxd">
            {painPoints.map((point, index) => <motion.p key={point} initial={{
            opacity: 0,
            x: -12
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.25,
            delay: Math.min(index * 0.04, 0.3),
            ease: [0.23, 1, 0.32, 1]
          }} className="flex items-start gap-3 px-5 py-4 text-slate-700 text-sm leading-relaxed border-t first:border-t-0 border-[#D5DAE0]" data-id="element-209" data-mp="o604bh6sacdj" data-mpi={index}>
                <X size={16} className="text-[#E8590C] shrink-0 mt-0.5" aria-hidden="true" data-id="element-210" data-mp="59h0n7er1god" />
                {point}
              </motion.p>)}
          </div>

          <div className="text-center mt-12 pt-8 border-t border-[#D5DAE0]" data-id="element-211" data-mp="3rnqmb8raoalg">
            <p className="text-2xl font-bold tracking-tight text-[#16191E]" data-id="element-212" data-mp="3mong3kzkvybw">
              You don’t need more scattered welding teaching resources.
            </p>
            <p className="text-xl mt-4 text-slate-700 font-semibold uppercase" data-id="element-213" data-mp="3dn7g876e3elx">
              You need a complete <span className="text-[#E8590C]" data-id="element-214" data-mp="1hk1bauqg3ugv">professional system</span> that shows
              you:
            </p>
            <ul className="mt-5 space-y-1.5" data-id="element-215" data-mp="1lm63rq55ql1x">
              {systemShows.map(line => <li key={line} className="text-lg md:text-xl font-extrabold tracking-wide text-[#16191E]" data-id="element-216" data-mp="17uhq3if9foiu">
                  {line}
                </li>)}
            </ul>
          </div>
        </motion.div>
      </div>
    </section>;
}